// ── CSV utilities ───────────────────────────────────────────────────
//
// Minimal dependency-free CSV builder + downloader used by the Reports
// page to export chart data. Handles quoting of values that contain
// commas, quotes, or newlines. Values null/undefined render as empty
// cells.

function escapeCell(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  if (/[,"\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Convert an array of objects to a CSV string. */
export function toCsv<T extends Record<string, any>>(
  rows: T[],
  headers?: { key: keyof T; label: string }[],
): string {
  if (rows.length === 0 && !headers) return '';
  const hdrs = headers ?? Object.keys(rows[0]).map((k) => ({ key: k as keyof T, label: k }));
  const headerLine = hdrs.map((h) => escapeCell(h.label)).join(',');
  const lines = rows.map((r) => hdrs.map((h) => escapeCell(r[h.key])).join(','));
  return [headerLine, ...lines].join('\n');
}

/**
 * Trigger a browser download of the given CSV content. Filename gets
 * an ISO date suffix so repeated exports don't overwrite each other
 * in the Downloads folder.
 */
export function downloadCsv(baseName: string, csv: string): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `${baseName}_${stamp}.csv`;
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Shortcut: build CSV and download in one call. */
export function exportCsv<T extends Record<string, any>>(
  baseName: string,
  rows: T[],
  headers?: { key: keyof T; label: string }[],
): void {
  downloadCsv(baseName, toCsv(rows, headers));
}
